import { useId } from 'react';
import { Popover, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { space } from '@viq-admin/ui';

/**
 * The three explanatory affordances for the Clean/Match/Send flow. Every
 * string they render comes from `CRM_COPY.help` — nothing in this file is
 * user-facing copy, so the "renamable in one place" invariant `copy.ts`
 * documents holds for the hint layer too.
 *
 * Closure-safe like the rest of `packages/*`: no fetch, no endpoint names, no
 * Refine/CASL/react-router. Pure presentation over injected strings.
 *
 * Choosing between them:
 *  - room under a heading, and everyone needs it  → `HintLine`
 *  - a small inline object (Tag / link / control) → `HelpHint`
 *  - the answer is a list of three or more cases  → `HelpPopover`
 */

/**
 * Off-screen but readable by assistive tech. `@viq-admin/ui` has no such
 * helper, so it is defined here rather than invented as a shared token for a
 * single consumer. `clip` (not `display: none`) is what keeps the text in the
 * accessibility tree.
 */
const VISUALLY_HIDDEN: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

/**
 * A real `<button>`, not a bare icon `<span>`: antd forwards its trigger
 * handlers to whatever child it is given, and a `<span>` is not
 * keyboard-focusable — a tooltip hung on one is mouse-only.
 */
const TRIGGER_BASE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  margin: 0,
  border: 0,
  background: 'none',
  color: 'inherit',
  opacity: 0.6,
  lineHeight: 1,
  fontSize: 'inherit',
  verticalAlign: 'baseline',
};

type Placement = React.ComponentProps<typeof Tooltip>['placement'];

/**
 * A DOM id safe to put in a CSS selector. `useId` returns values containing
 * colons (`:r3:`) — legal in an HTML id, but React's own docs warn they are
 * not valid selector syntax, and anything that later resolves the id as one
 * throws on it. Stripping the punctuation keeps the per-instance uniqueness
 * that is the only reason to use the hook.
 */
function useDomId(prefix: string): string {
  return `${prefix}-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
}

export interface HintLineProps {
  text: string;
  testId: string;
  /** Trailing help triggers, for a hint whose sentence needs footnotes. */
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

/**
 * An always-visible lead-in under a tab or a section heading. No interaction,
 * so nothing to make reachable — it is simply on screen for everyone. Used
 * where the explanation is what a first-time operator needs before they touch
 * anything, rather than an answer to a question they went looking for.
 */
export function HintLine({ text, testId, children, style }: HintLineProps) {
  return (
    <Typography.Paragraph
      type="secondary"
      data-testid={testId}
      style={{ marginTop: 0, marginBottom: space.md, maxWidth: 720, ...style }}
    >
      {text}
      {children}
    </Typography.Paragraph>
  );
}

export interface HelpHintProps {
  /** The trigger's accessible name. */
  label: string;
  /** The hint itself — the tooltip body AND the trigger's description. */
  text: string;
  testId: string;
  placement?: Placement;
}

/**
 * A "?" beside a small inline object, explaining it on hover, on focus, and to
 * a screen reader without either.
 *
 * The trigger button is wrapped in a `<span>` deliberately: rc-tooltip clones
 * its child and OVERWRITES that child's `aria-describedby` with the overlay's
 * id — and the overlay is not in the DOM until the tooltip first opens, so on
 * its own it describes the trigger as nothing at all. Giving the Tooltip the
 * span leaves the button's own `aria-describedby` — pointing at the hidden
 * copy of the text below — intact. React delegates focus through `focusin`,
 * which bubbles, so the span still sees the inner button's focus.
 */
export function HelpHint({ label, text, testId, placement }: HelpHintProps) {
  const descriptionId = useDomId('crm-help');
  return (
    // antd's default trigger is 'hover' ALONE — without 'focus' the tooltip
    // never opens for a keyboard user.
    <Tooltip title={text} trigger={['hover', 'focus']} placement={placement ?? 'top'}>
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        <button
          type="button"
          aria-label={label}
          aria-describedby={descriptionId}
          data-testid={testId}
          style={{ ...TRIGGER_BASE, cursor: 'help' }}
        >
          <QuestionCircleOutlined aria-hidden="true" />
        </button>
        <span id={descriptionId} style={VISUALLY_HIDDEN}>
          {text}
        </span>
      </span>
    </Tooltip>
  );
}

export interface HelpPopoverProps {
  label: string;
  title: string;
  lines: readonly string[];
  testId: string;
  placement?: Placement;
}

/**
 * The list form, for the three places where the honest answer is three or more
 * cases (why a lead isn't ready, what the two merge choices do, what each send
 * result means). A Popover for one sentence would be heavier than the content
 * warrants — those stay `HelpHint`s.
 *
 * Click only, no focus trigger: a real `<button>` already activates on Enter
 * and Space, so click is fully keyboard-operable, and the hidden description
 * carries the whole list to a screen reader without opening anything. A focus
 * trigger would instead pop each panel open as the operator tabs past it.
 */
export function HelpPopover({ label, title, lines, testId, placement }: HelpPopoverProps) {
  const descriptionId = useDomId('crm-help');
  return (
    <Popover
      title={title}
      trigger={['click']}
      placement={placement ?? 'top'}
      content={
        <ul style={{ maxWidth: 360, margin: 0, paddingInlineStart: space.lg }}>
          {lines.map((line) => (
            <li key={line} style={{ marginBottom: space.xs }}>
              <Typography.Text>{line}</Typography.Text>
            </li>
          ))}
        </ul>
      }
    >
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        <button
          type="button"
          aria-label={label}
          aria-describedby={descriptionId}
          data-testid={testId}
          style={{ ...TRIGGER_BASE, cursor: 'pointer' }}
        >
          <QuestionCircleOutlined aria-hidden="true" />
        </button>
        <span id={descriptionId} style={VISUALLY_HIDDEN}>
          {`${title} ${lines.join(' ')}`}
        </span>
      </span>
    </Popover>
  );
}
